import React, { useState, useEffect } from "react";

const STEPS = [
  {
    id: "welcome",
    icon: "⬡",
    title: "WELCOME TO CREATOR COMMANDOS",
    body: "Your command center for serious YouTube content. Let's get you operational in under 2 minutes.",
    action: "Begin mission →",
  },
  {
    id: "channel",
    icon: "◎",
    title: "CONNECT YOUR CHANNEL",
    body: "Click + Add Channel in the sidebar to connect your YouTube channel. You'll need a free YouTube Data API key from Google Cloud Console.",
    action: "Got it →",
    tip: "Get a free API key: console.cloud.google.com → APIs & Services → Enable YouTube Data API v3 → Credentials → Create API key.",
  },
  {
    id: "episodes",
    icon: "▤",
    title: "BROWSE YOUR EPISODES",
    body: "Your full video library loads automatically. Click any episode to open the detail panel — add transcripts, generate AI clip suggestions, and track distribution from there.",
    action: "Got it →",
  },
  {
    id: "platforms",
    icon: "⊕",
    title: "CHOOSE YOUR PLATFORMS",
    body: "Head to the Connections tab to choose which platforms you distribute clips to — YouTube Shorts, TikTok, and Instagram. Only enable the ones you actually use.",
    action: "Got it →",
    tip: "TikTok and Instagram posting require API approval. Tap 'How to apply' in the Connections tab for step-by-step instructions.",
  },
  {
    id: "ai",
    icon: "✦",
    title: "USE AI FEATURES",
    body: "Each AI action costs 1 credit. Free accounts get 25 credits per month — use them on clip suggestions, transcript cleaning, comment analysis, and branding guide generation.",
    action: "Got it →",
    tip: "Credits reset on the 1st of every month. Upgrade your plan anytime for more.",
  },
  {
    id: "analytics",
    icon: "◉",
    title: "TRACK YOUR PERFORMANCE",
    body: "The Analytics tab shows views over time, best posting days, and top performers. The Calendar tab maps every episode to its publish date with clip status overlaid.",
    action: "Let's go →",
  },
];

const STORAGE_KEY = "cc_onboarding_complete";

export default function OnboardingModal({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) setVisible(true);
  }, []);

  const next = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      finish();
    }
  };

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    onComplete?.();
  };

  if (!visible) return null;

  const current = STEPS[step];
  const progress = ((step) / (STEPS.length - 1)) * 100;

  const s = {
    overlay: {
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 2000, padding: 20,
    },
    modal: {
      background: "#080C12",
      border: "1px solid #1A2940",
      borderRadius: 10,
      width: "100%", maxWidth: 480,
      overflow: "hidden",
    },
    progress: {
      height: 3, background: "#1A2940",
    },
    progressBar: {
      height: "100%",
      width: `${progress}%`,
      background: "#C9A227",
      transition: "width 0.3s",
    },
    body: { padding: 32, textAlign: "center" },
    icon: {
      fontSize: 48, color: "#C9A227",
      marginBottom: 16, display: "block",
    },
    title: {
      fontFamily: "Impact, 'Arial Black', sans-serif",
      fontSize: 20, letterSpacing: "0.08em",
      color: "#E8EBF0", marginBottom: 16,
    },
    text: {
      fontSize: 14, color: "#8899AA",
      lineHeight: 1.7, marginBottom: 24,
    },
    tip: {
      background: "rgba(43,94,167,0.1)",
      border: "1px solid rgba(43,94,167,0.3)",
      borderRadius: 6, padding: "10px 14px",
      fontSize: 12, color: "#5A6A7E",
      lineHeight: 1.6, marginBottom: 24,
      textAlign: "left",
    },
    btn: {
      display: "inline-block",
      padding: "12px 32px",
      background: "#C9A227",
      border: "none", borderRadius: 4,
      color: "#080C12",
      fontFamily: "inherit",
      fontSize: 13, fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      cursor: "pointer",
      marginBottom: 16,
    },
    skip: {
      display: "block",
      background: "none", border: "none",
      color: "#3A4A5E", fontFamily: "inherit",
      fontSize: 11, cursor: "pointer",
    },
    dots: {
      display: "flex", justifyContent: "center",
      gap: 6, marginBottom: 24,
    },
    dot: (active) => ({
      width: active ? 20 : 6,
      height: 6, borderRadius: 3,
      background: active ? "#C9A227" : "#1A2940",
      transition: "all 0.3s",
    }),
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.progress}>
          <div style={s.progressBar} />
        </div>
        <div style={s.body}>
          <span style={s.icon}>{current.icon}</span>
          <div style={s.title}>{current.title}</div>
          <div style={s.text}>{current.body}</div>
          {current.tip && (
            <div style={s.tip}>💡 {current.tip}</div>
          )}
          <div style={s.dots}>
            {STEPS.map((_, i) => (
              <div key={i} style={s.dot(i === step)} />
            ))}
          </div>
          <button style={s.btn} onClick={next}>
            {current.action}
          </button>
          <button style={s.skip} onClick={finish}>
            Skip tour
          </button>
        </div>
        <div style={{ height: 3, background: "#C9A227" }} />
      </div>
    </div>
  );
}

// Hook to reset onboarding (for testing)
export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
}
